// Logto OIDC configuration for the web SPA (unified-auth migration, Phase 4).
//
// The client authenticates against Logto directly via Authorization Code + PKCE
// (@logto/react), then exchanges the resulting Logto ACCESS TOKEN for an
// IOSentra session at POST /auth/session. This module centralizes the config +
// the AUTH_FLOW gate so LoginPage / callback / AuthContext all read one source.
//
// AUTH_FLOW mirrors the backend flag:
//   'proxy' — the existing password-form → /auth/logto/start path (default).
//   'oidc'  — the SDK redirect flow described above.
// Set VITE_AUTH_FLOW=oidc (and the VITE_LOGTO_* vars) to enable the new flow.

import type { LogtoConfig } from '@logto/react';

export const AUTH_FLOW: 'proxy' | 'oidc' =
  import.meta.env.VITE_AUTH_FLOW === 'oidc' ? 'oidc' : 'proxy';

/** True when the SDK-driven Logto login should be used instead of the proxy form. */
export const isOidcFlow = AUTH_FLOW === 'oidc';

// The IOSentra API resource indicator (must match the backend LOGTO_API_RESOURCE
// and what logto-mfa-setup.mjs registered) — requesting it makes Logto mint the
// access token with the audience the API validates.
export const API_RESOURCE =
  import.meta.env.VITE_LOGTO_API_RESOURCE || 'https://api.iosentra.com';

export const logtoConfig: LogtoConfig = {
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || 'http://localhost:3301',
  appId: import.meta.env.VITE_LOGTO_APP_ID || '',
  // Request the IOSentra API resource so the issued access token carries the
  // audience the backend pins. `scopes` requests the baseline access scope.
  resources: [API_RESOURCE],
  scopes: ['iosentra:access'],
};

export const SIGN_IN_REDIRECT_URI =
  import.meta.env.VITE_LOGTO_REDIRECT_URI ||
  `${window.location.origin}/callback`;

export const POST_SIGN_OUT_REDIRECT_URI =
  import.meta.env.VITE_LOGTO_POST_LOGOUT_URI || window.location.origin;
